// Shared company create/edit modal. Extracted from Accounts.jsx (issue #177) so
// the create flow (Accounts page) and the edit flow (AccountDetail page) use the
// SAME form — ending the field-drift bug where the edit modal pre-filled only a
// subset of fields and wiped the rest on save.
//
// Edit mode: pass `account={...}`. The form hydrates from the account, the title
// becomes "Edit Company", the button "Update Company", and the duplicate gate is
// disabled (checkDuplicate returns early when `account` is set).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Info, Loader2, X, Check, Sparkles
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import useCustomFields from '../../hooks/useCustomFields';
import { useDirtyFields } from '../../hooks/useDirtyFields';
import useReturnFocus from '../../hooks/useReturnFocus';
import CustomFieldRenderer from '../CustomFieldRenderer';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import { missingRequiredCustomFields } from '../custom-fields/requiredFields';
import { INDUSTRIES, SIZES, INVESTOR_TYPES, EMPLOYEE_RANGES, STATUSES, toIndustryArray, normalizeStatus } from './companyConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Empty-form defaults. This object is also the allow-list of fields the modal
// submits (see `handleSave`), so every field the form renders must have a key
// here — and nothing else should.
const EMPTY_FORM = {
    name: '', domain: '', industry: '', size: 'smb', annual_revenue: '', num_employees: '',
    website: '', phone: '', email: '',
    address: '', city: '', state: '', country: 'US', region: '', hq_country: '',
    description: '', status: 'prospect',
    linkedin_url: '', twitter_url: '', founded_year: '', funding_raised: '', employee_range: '',
    lead_investor: '', investor_type: '',
    custom_fields: {},
};

// ─── Status Selector with Info ─────────────────────────────────────────────

function StatusSelector({ value, onChange }) {
    const [showInfo, setShowInfo] = useState(null);

    return (
        <div>
            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Status</label>
            <div className="flex flex-wrap gap-1.5">
                {STATUSES.map(s => (
                    <div key={s.value} className="relative">
                        <div className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all cursor-pointer ${
                                value === s.value
                                    ? `${s.color} ring-2 ring-offset-1 ring-current`
                                    : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
                            }`}
                            onClick={() => onChange(s.value)}>
                            {s.label}
                            <span onClick={(e) => { e.stopPropagation(); setShowInfo(showInfo === s.value ? null : s.value); }}
                                className="opacity-40 hover:opacity-100 cursor-help ml-0.5">
                                <Info className="w-3 h-3" />
                            </span>
                        </div>
                        {showInfo === s.value && (
                            <div className="absolute z-20 bottom-full mb-2 left-0 w-64 bg-white rounded-lg shadow-xl border border-stone-200 p-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${s.color}`}>{s.label}</span>
                                    <span onClick={() => setShowInfo(null)} className="text-stone-400 hover:text-stone-600 cursor-pointer text-xs">✕</span>
                                </div>
                                <p className="text-xs text-stone-600 leading-relaxed">{s.desc}</p>
                                <div className="absolute -bottom-1.5 left-4 w-3 h-3 bg-white border-r border-b border-stone-200 rotate-45" />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Industry Multi-Select ─────────────────────────────────────────────────

function IndustryMultiSelect({ value = [], onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [customInput, setCustomInput] = useState('');

    const filtered = INDUSTRIES.filter(i =>
        i.toLowerCase().includes(search.toLowerCase()) && !value.includes(i)
    );

    const add = (industry) => { onChange([...value, industry]); };
    const remove = (industry) => { onChange(value.filter(v => v !== industry)); };
    const addCustom = () => {
        const v = customInput.trim();
        if (v && !value.includes(v)) { onChange([...value, v]); }
        setCustomInput('');
    };

    return (
        <div>
            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Industry</label>
            {/* Selected tags */}
            <div className="flex flex-wrap gap-1 mb-1.5 min-h-[28px]">
                {value.map(v => (
                    <span key={v} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-0.5 rounded-full border border-stone-200">
                        {v}
                        <button onClick={() => remove(v)} className="text-stone-400 hover:text-red-500 ml-0.5">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                {value.length === 0 && <span className="text-xs text-stone-400 py-0.5">No industries selected</span>}
            </div>
            {/* Dropdown trigger */}
            <button type="button" onClick={() => setOpen(!open)}
                className="w-full px-3 py-2 text-sm text-left border border-stone-200 rounded-lg bg-white hover:bg-stone-50 text-stone-500 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                {open ? 'Close' : '+ Add industry...'}
            </button>
            {/* Dropdown */}
            {open && (
                <div className="mt-1 border border-stone-200 rounded-lg bg-white shadow-lg max-h-48 overflow-hidden">
                    <div className="p-2 border-b border-stone-100">
                        <input value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search industries..." autoFocus
                            className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded bg-stone-50 focus:outline-none" />
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                        {filtered.slice(0, 20).map(i => (
                            <button key={i} type="button" onClick={() => { add(i); setSearch(''); }}
                                className="w-full px-3 py-1.5 text-xs text-left text-stone-700 hover:bg-stone-50 truncate">
                                {i}
                            </button>
                        ))}
                        {filtered.length > 20 && <p className="px-3 py-1 text-[10px] text-stone-400">Type to narrow down...</p>}
                        {filtered.length === 0 && search && (
                            <div className="px-3 py-2">
                                <p className="text-[10px] text-stone-400 mb-1">No match found.</p>
                                <div className="flex gap-1">
                                    <input value={customInput || search} onChange={e => setCustomInput(e.target.value)}
                                        className="flex-1 px-2 py-1 text-xs border border-stone-200 rounded" placeholder="Custom industry..." />
                                    <button type="button" onClick={() => { add(customInput || search); setCustomInput(''); setSearch(''); }}
                                        className="px-2 py-1 text-xs bg-stone-900 text-white rounded">Add</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Company Modal ──────────────────────────────────────────────────────────

export default function CompanyModal({ open, onClose, account, onSave }) {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY_FORM);
    const [baseline, setBaseline] = useState(EMPTY_FORM); // snapshot at open for dirty comparison (issue #306)
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    // The close-confirm opens from state, with no Radix trigger to return focus to.
    const closeConfirmFocus = useReturnFocus();
    const openCloseConfirm = () => { closeConfirmFocus.capture(); setShowCloseConfirm(true); };
    const { dirtyCount, isFieldDirty } = useDirtyFields(form, baseline);
    const isDirty = dirtyCount > 0;
    const { fields: customFieldDefs } = useCustomFields('account');
    const [loading, setLoading] = useState(false);
    // Set by a Save that a Required custom field blocked; the inline errors then follow the values.
    const [showRequired, setShowRequired] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [enrichSteps, setEnrichSteps] = useState([]);
    const [duplicate, setDuplicate] = useState(null); // check-duplicate payload
    const [claiming, setClaiming] = useState(false);

    // Re-seed on every open, not just when `account` changes identity. The edit
    // consumer (AccountDetail) holds one stable `account` object for the life of
    // the page, so keying only on `account` left abandoned edits in the form
    // after Cancel — reopening showed the unsaved values instead of the record.
    useEffect(() => {
        if (!open) return;
        let init;
        if (account) init = {
            ...EMPTY_FORM,
            ...account,
            status: normalizeStatus(account.status) || EMPTY_FORM.status,
            custom_fields: account.custom_fields || {},
        };
        else init = EMPTY_FORM;
        setForm(init);
        setBaseline(init);
        setDuplicate(null);
        setShowCloseConfirm(false);
        setShowRequired(false);
    }, [account, open]);

    // Duplicate gate (new companies only): the same company URL must never be
    // added twice. Allocated → blocked; unallocated → offer takeover.
    const checkDuplicate = async (url) => {
        if (account) return null; // editing an existing company
        try {
            const res = await axios.post(`${API}/accounts/check-duplicate`,
                { url }, { headers: getAuthHeader() });
            if (res.data?.exists) {
                setDuplicate(res.data);
                return res.data;
            }
            setDuplicate(null);
            return null;
        } catch {
            return null; // check is best-effort; the create endpoint still enforces
        }
    };

    const handleClaim = async () => {
        if (!duplicate?.account_id) return;
        setClaiming(true);
        try {
            const res = await axios.post(`${API}/accounts/${duplicate.account_id}/claim`,
                {}, { headers: getAuthHeader() });
            toast.success(`You now own ${duplicate.name}`);
            onSave(res.data);
            onClose();
            navigate(`/companies/${duplicate.account_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to take ownership');
        } finally {
            setClaiming(false);
        }
    };

    const handleEnrich = async () => {
        let url = (form.website || '').trim();
        if (!url) return toast.error('Enter a website URL first');
        if (!url.startsWith('http')) url = `https://${url}`;

        // Check before spending AI credits — and before the coach invests time.
        const dup = await checkDuplicate(url);
        if (dup) {
            if (dup.allocated) return; // banner explains; can't proceed
            // Unallocated: banner offers takeover; don't enrich a duplicate.
            return;
        }

        setEnriching(true);
        setEnrichSteps([{ text: 'Connecting to AI research agent...', done: false }]);

        try {
            // Step 1: Basic enrichment
            setTimeout(() => setEnrichSteps(s => [
                { ...s[0], done: true },
                { text: 'Scraping website content...', done: false }
            ]), 800);

            setTimeout(() => setEnrichSteps(s => {
                if (s.length < 3) return [...s.map(x => ({ ...x, done: true })), { text: 'Analyzing with AI + website data...', done: false }];
                return s;
            }), 3000);

            const res = await axios.post(`${API}/enrichment/url`, { url }, { headers: getAuthHeader(), timeout: 60000 });
            const data = res.data?.data;
            const sources = data?._sources || [];
            const warnings = data?._warnings || [];

            // Show sources used
            const sourceLabels = sources.filter(s => s !== 'ai_knowledge').map(s => ({
                website_scrape: 'Website', exa: 'Exa', linkedin_apify: 'LinkedIn'
            }[s] || s));
            setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })),
                { text: `Sources: ${sourceLabels.join(' + ')} + AI`, done: true },
                { text: 'Mapping company data...', done: false }
            ]);

            // Show warnings for disconnected/failed sources
            if (warnings.length > 0) {
                setTimeout(() => {
                    setEnrichSteps(s => [
                        ...s,
                        ...warnings.map(w => ({ text: `⚠ ${w.msg}`, done: true, warning: true }))
                    ]);
                }, 500);
            }

            if (data) {
                // Apply basic fields
                setForm(prev => {
                    const updated = { ...prev };
                    Object.entries(data).forEach(([key, val]) => {
                        if (val !== null && val !== undefined && val !== '' && !prev[key]) {
                            updated[key] = val;
                        }
                    });
                    return updated;
                });

                // Step 2: Deep research for description
                setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })), { text: 'Conducting deep company research...', done: false }]);

                try {
                    const deepRes = await axios.post(`${API}/enrichment/url`, {
                        url,
                        deep: true,
                    }, { headers: getAuthHeader(), timeout: 45000 });
                    const deepData = deepRes.data?.data;
                    if (deepData?.description) {
                        setForm(prev => ({ ...prev, description: deepData.description }));
                    }
                } catch {}

                setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })), { text: 'Research complete!', done: true }]);
                toast.success('AI enrichment complete — review the auto-filled fields');
            }
        } catch (err) {
            const detail = err.response?.data?.detail || 'Enrichment failed';
            const is429 = err.response?.status === 429 || detail.includes('quota') || detail.includes('429');
            if (is429) {
                toast.error('AI credits exhausted — check OpenAI billing at platform.openai.com', { duration: 8000 });
            } else {
                toast.error(detail);
            }
            setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })), { text: is429 ? 'AI credits exhausted — add credits at platform.openai.com' : 'Research failed', done: true, error: true }]);
        } finally {
            setTimeout(() => { setEnriching(false); setEnrichSteps([]); }, 5000);
        }
    };

    const handleSave = async () => {
        if (!form.name) return toast.error('Company name is required');
        // D4 (#972): a Required custom field must be filled in the app.
        if (Object.keys(missingRequiredCustomFields(customFieldDefs, form.custom_fields)).length) {
            setShowRequired(true);
            return toast.error('Fill in the required custom fields');
        }
        setLoading(true);
        // Submit only the fields this modal renders. In edit mode `form` is
        // hydrated from the whole account document, which also carries fields the
        // modal has no UI for — tags, locations, owned_products, recent_news,
        // strategic_priorities, health_score. Echoing those back re-validates them
        // on every save (one malformed legacy owned_products/recent_news entry
        // would 422 the entire edit), drops any key their nested models don't
        // declare, and fires the PUT handler's tag-history/event-bus branch for a
        // tag set the user never touched.
        const payload = {};
        Object.keys(EMPTY_FORM).forEach(k => { payload[k] = form[k]; });
        // Clean empty strings from numeric fields to avoid Pydantic validation errors
        ['annual_revenue', 'num_employees', 'founded_year'].forEach(k => {
            if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k];
        });
        try {
            let res;
            if (account) {
                res = await axios.put(`${API}/accounts/${account.account_id}`, payload, { headers: getAuthHeader() });
            } else {
                res = await axios.post(`${API}/accounts`, payload, { headers: getAuthHeader() });
            }
            toast.success(account ? 'Company updated' : 'Company created');
            onSave(res.data);
            onClose();
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 && detail?.code === 'duplicate_company') {
                setDuplicate(detail);
                toast.error(detail.allocated
                    ? `${detail.name} already exists and is allocated to ${detail.owner_name || 'another rep'}`
                    : `${detail.name} already exists — you can take ownership instead`);
            } else {
                toast.error('Failed to save company');
            }
        }
        finally { setLoading(false); }
    };

    const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20";

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && isDirty) { openCloseConfirm(); return; } if (!nextOpen) onClose(); }}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
                <DialogHeader className="px-6 pt-5 pb-3 shrink-0 border-b border-stone-100">
                    <DialogTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {account ? 'Edit Company' : 'New Company'}
                        {isDirty && (
                            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full ml-2" data-testid="modal-unsaved-badge">
                                <span className="mr-1">{dirtyCount}</span>unsaved
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {/* Duplicate company gate */}
                    {duplicate && (
                        <div className={`rounded-xl border p-4 ${duplicate.allocated ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                            <div className="flex items-start gap-2">
                                <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${duplicate.allocated ? 'text-red-500' : 'text-amber-500'}`} />
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${duplicate.allocated ? 'text-red-700' : 'text-amber-700'}`}>
                                        {duplicate.name} already exists in the CRM
                                    </p>
                                    <p className={`text-xs mt-0.5 ${duplicate.allocated ? 'text-red-600' : 'text-amber-600'}`}>
                                        {duplicate.allocated_to_me
                                            ? 'This company is already allocated to you — open it to keep working on it.'
                                            : duplicate.allocated
                                                ? `This company is allocated to ${duplicate.owner_name || 'another rep'}. You can't proceed with a duplicate.`
                                                : 'This company is not allocated to anyone yet — you can take ownership instead of creating a duplicate.'}
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                        <button onClick={() => { onClose(); navigate(`/companies/${duplicate.account_id}`); }}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-200 bg-white text-stone-700 hover:bg-stone-50">
                                            View company
                                        </button>
                                        {duplicate.claimable && (
                                            <button onClick={handleClaim} disabled={claiming}
                                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
                                                {claiming && <Loader2 className="w-3 h-3 animate-spin" />}
                                                Take ownership
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Website URL + AI Enrich */}
                    <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-4">
                        <label className="block text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1.5">Website URL — AI Auto-Fill</label>
                        <div className="flex gap-2">
                            <input value={form.website || ''} onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                                placeholder="https://company.com" className={`flex-1 ${INPUT}`} />
                            <button onClick={handleEnrich} disabled={enriching || !form.website}
                                className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition-colors whitespace-nowrap shadow-sm">
                                {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {enriching ? 'Researching...' : 'AI Enrich'}
                            </button>
                        </div>
                        {!account && !enriching && <p className="text-[10px] text-amber-600/70 mt-1">Paste a URL and click AI Enrich to auto-fill all company details</p>}

                        {/* Enrichment progress */}
                        {enrichSteps.length > 0 && (
                            <div className="mt-3 space-y-1">
                                {enrichSteps.map((step, i) => (
                                    <div key={i} className={`flex items-center gap-2 text-xs ${
                                        step.error ? 'text-red-600' :
                                        step.warning ? 'text-amber-600' :
                                        step.done ? 'text-emerald-600' : 'text-amber-700'
                                    }`}>
                                        {step.warning ? (
                                            <Info className="w-3.5 h-3.5 flex-shrink-0" />
                                        ) : step.done ? (
                                            step.error ? <X className="w-3.5 h-3.5 flex-shrink-0" /> : <Check className="w-3.5 h-3.5 flex-shrink-0" />
                                        ) : (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                                        )}
                                        <span>{step.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 3-column grid for core fields */}
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: 'Company Name *', key: 'name', placeholder: 'Acme Corp' },
                            { label: 'Company Phone', key: 'phone', placeholder: '+1-555-0100' },
                            { label: 'Company Email', key: 'email', placeholder: 'info@acme.com', type: 'email' },
                            { label: 'Street Address', key: 'address', placeholder: '123 Main St' },
                            { label: 'City', key: 'city', placeholder: 'New York' },
                            { label: 'State / Province', key: 'state', placeholder: 'NY' },
                            { label: 'Country', key: 'country', placeholder: 'US' },
                            { label: 'Region', key: 'region', placeholder: 'North America' },
                            { label: 'Company HQ Country', key: 'hq_country', placeholder: 'United States' },
                            { label: 'Founded Year', key: 'founded_year', placeholder: '2020', type: 'number' },
                            { label: 'Funding Raised', key: 'funding_raised', placeholder: '$50M' },
                            { label: 'Annual Revenue', key: 'annual_revenue', placeholder: '1000000', type: 'number' },
                            // Not type="number": the backend types num_employees as
                            // Optional[Any] and the staging commit path stores headcount
                            // ranges ("1001-5000") here, which a number input renders blank.
                            { label: 'Employee Number Approx', key: 'num_employees', placeholder: '250' },
                            { label: 'Lead Investor', key: 'lead_investor', placeholder: 'Sequoia Capital' },
                        ].map(f => (
                            <div key={f.key}>
                                <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">{f.label}</label>
                                <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                                    placeholder={f.placeholder} className={INPUT} />
                            </div>
                        ))}
                        {/* Investor Type — dropdown */}
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Investor Type</label>
                            <select value={form.investor_type || ''} onChange={e => setForm(p => ({ ...p, investor_type: e.target.value }))}
                                className={`${INPUT} bg-white`}>
                                <option value="">Select...</option>
                                {INVESTOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        {/* Employee Range keeps its slot */}
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Employee Range</label>
                            <select value={form.employee_range || ''} onChange={e => {
                                const sel = EMPLOYEE_RANGES.find(r => r.range === e.target.value);
                                setForm(p => ({ ...p, employee_range: e.target.value, size: sel ? sel.size.toLowerCase() : p.size }));
                            }} className={`${INPUT} bg-white`}>
                                <option value="">Select...</option>
                                {EMPLOYEE_RANGES.map(r => (
                                    <option key={r.range} value={r.range}>{r.category} — {r.range}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Category badge */}
                    {form.employee_range && (() => {
                        const sel = EMPLOYEE_RANGES.find(r => r.range === form.employee_range);
                        return sel ? (
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${sel.color}`}>
                                {sel.category} · {sel.size} · {sel.range} employees
                            </div>
                        ) : null;
                    })()}

                    {/* Social URLs — 2 columns */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Company LinkedIn URL</label>
                            <input value={form.linkedin_url || ''} onChange={e => setForm(p => ({ ...p, linkedin_url: e.target.value }))}
                                placeholder="https://linkedin.com/company/..." className={INPUT} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Twitter URL</label>
                            <input value={form.twitter_url || ''} onChange={e => setForm(p => ({ ...p, twitter_url: e.target.value }))}
                                placeholder="https://twitter.com/..." className={INPUT} />
                        </div>
                    </div>

                    {/* Industry + Size side by side */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                            <IndustryMultiSelect
                                value={toIndustryArray(form.industry)}
                                onChange={v => setForm(p => ({ ...p, industry: v }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Size</label>
                            <select value={form.size} onChange={e => setForm(p => ({ ...p, size: e.target.value }))}
                                className={`${INPUT} bg-white capitalize`}>
                                {SIZES.map(s => <option key={s} className="capitalize">{s}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Status */}
                    <StatusSelector value={form.status} onChange={v => setForm(p => ({ ...p, status: v }))} />

                    {/* Description */}
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Description</label>
                        {form.description && !enriching ? (
                            <div className="border border-stone-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 bg-stone-50/50 max-h-[200px] overflow-y-auto">
                                    {form.description.split('\n\n').map((para, i) => (
                                        <p key={i} className="text-sm text-stone-700 leading-relaxed mb-2 last:mb-0">{para}</p>
                                    ))}
                                </div>
                                <button type="button" onClick={() => {
                                    const el = document.getElementById('desc-edit');
                                    if (el) el.classList.toggle('hidden');
                                }} className="w-full px-4 py-1.5 text-[10px] text-stone-400 hover:text-stone-600 bg-white border-t border-stone-100 text-center">
                                    Click to edit
                                </button>
                                <textarea id="desc-edit" value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={8}
                                    className={`${INPUT} hidden rounded-none border-0 border-t border-stone-100`} />
                            </div>
                        ) : (
                            <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={6}
                                className={INPUT} placeholder="Company overview, products/services, market position, key differentiators..." />
                        )}
                    </div>

                    {customFieldDefs.length > 0 && (
                        <div className="pt-2 border-t border-stone-100 mt-2">
                            <label className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2 mt-2">
                                Custom Fields <CustomFieldBadge />
                            </label>
                            <CustomFieldRenderer
                                fields={customFieldDefs}
                                values={form.custom_fields || {}}
                                editing={true}
                                errors={showRequired ? missingRequiredCustomFields(customFieldDefs, form.custom_fields) : {}}
                                onChange={(newCustomFields) => setForm(prev => ({ ...prev, custom_fields: newCustomFields }))}
                            />
                        </div>
                    )}

                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 shrink-0 px-6 py-4 border-t border-stone-100">
                    <button onClick={() => { if (isDirty) openCloseConfirm(); else onClose(); }} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} className="px-5 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 font-medium">
                        {loading ? 'Saving...' : account ? 'Update Company' : 'Create Company'}
                    </button>
                </div>

                {/* A Radix dialog nested in this one, not a plain fixed div. While
                    the edit dialog is open Radix sets `pointer-events: none` on
                    body and traps focus inside it, so a sibling overlay could be
                    neither clicked nor tabbed to, and it painted under the dialog
                    whenever the page root was a stacking context (#496). A nested
                    Radix layer becomes the top layer: it takes pointer events and
                    focus, and Escape closes only the confirm. Stay or Escape puts
                    focus back where it was, not on <body>. */}
                <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                    <AlertDialogContent className="max-w-sm rounded-xl" data-testid="close-confirm-modal" onCloseAutoFocus={closeConfirmFocus.onCloseAutoFocus}>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="font-bold text-stone-900">Unsaved Changes</AlertDialogTitle>
                            <AlertDialogDescription className="text-stone-500">You have unsaved changes. Are you sure you want to close?</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="close-stay-btn">Stay</AlertDialogCancel>
                            <AlertDialogAction onClick={onClose} className="bg-red-500 text-white hover:bg-red-600" data-testid="close-discard-btn">Discard & Close</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
}
